import { useCallback, useId, useMemo } from 'react';
import classNames from 'classnames';

import { splitProps } from '@xemplo/common-util';
import { Country } from '@xemplo/country-helper';
import { InputErrorProps, InputSize, parseCssSizeVar } from '@xemplo/input-utils';
import { XemploInputProps } from '@xemplo/xemplo-input';

import { tryParsePhoneNumber } from '../helpers/phone-field.helper';
import { FlagPropsKeys, OPTIMIZED_COUNTRIES_COUNT } from '../phone-field.const';
import {
  FlagProps,
  NormalizedFlagsProps,
  PhoneFieldFlagMode,
  PhoneFieldProps,
} from '../phone-field.types';

function normalizeFlagMode({ flagMode, selectableCountries }: FlagProps) {
  if (flagMode !== undefined && flagMode !== PhoneFieldFlagMode.Auto) return flagMode;

  return selectableCountries && selectableCountries.length >= OPTIMIZED_COUNTRIES_COUNT
    ? PhoneFieldFlagMode.Sprite
    : PhoneFieldFlagMode.Individual;
}

/**
 * Hook responsible for defining the metadata of the phone field.
 * It handles all the received props, apply defaults and
 * separates them into specific props for each sub-component.
 *
 * @param props the full untreated phone field props
 * @returns
 */
export function usePhoneFieldMeta(props: PhoneFieldProps) {
  const id = useId();
  const [flagsProps, xemploInputProps] = splitProps(props, FlagPropsKeys);
  const { errorMessage, description, onValueChange, ...rest } = xemploInputProps;

  const inputId = `${id}${xemploInputProps.id ?? xemploInputProps.name}`;
  const fieldWrapperId = `${inputId}-field-wrapper`;
  const inputSize = xemploInputProps.inputSize ?? InputSize.Default;

  const defaultCountryCode = useMemo(() => {
    const baseCountryCode = flagsProps.defaultCountryCode ?? Country.Code.Australia;

    if (xemploInputProps.value) {
      const parsed = tryParsePhoneNumber(
        xemploInputProps.value.toString(),
        baseCountryCode,
      );
      return (parsed?.country as Country.Code) ?? baseCountryCode;
    }

    return baseCountryCode;
    // Intentionally ignoring dependency on xemploInputProps.value
    // as it would cause unnecessary recalculations and re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsProps.defaultCountryCode]);

  const flagProps = useMemo(() => {
    const flagMode = normalizeFlagMode(flagsProps);
    return {
      ...flagsProps,
      defaultCountryCode,
      flagMode,
      useSprite: flagMode === PhoneFieldFlagMode.Sprite,
    } as const satisfies NormalizedFlagsProps;
  }, [defaultCountryCode, flagsProps]);

  // This is a factory to create new input props with overrides
  // and it's done in this way to avoid recreating the whole object
  // on every render of the meta hook. This will be actually created
  // within the events hook and due to the spreading, it will ensure
  // that the reference will only change when something relevant changes.
  const inputPropsFactory = useCallback(
    (otherProps?: Partial<XemploInputProps>) => {
      return {
        ...rest,
        ...otherProps,
        autoComplete: 'tel-national',
        disableDescriptionOrError: true,
        errorMessage,
        id: inputId,
        inputMode: 'numeric',
        inputSize,
        placeholder: xemploInputProps.placeholder ?? ' ',
        type: 'tel',
        width: '100%',
      } satisfies XemploInputProps;
    },
    [rest, errorMessage, inputId, xemploInputProps.placeholder, inputSize],
  );

  const maybeErrorOrDescriptionProps = useMemo(() => {
    const hasError = !!errorMessage;
    const errorId = hasError ? `${inputId}-error` : undefined;
    const descriptionId = description ? `${inputId}-description` : undefined;

    return {
      description,
      descriptionProps: descriptionId ? { id: descriptionId } : undefined,
      error: hasError,
      errorMessage,
      errorMessageProps: errorId ? { id: errorId } : undefined,
      testId: inputId,
    } satisfies InputErrorProps;
  }, [description, errorMessage, inputId]);

  const countryPrefixProps = useMemo(() => {
    return {
      'aria-hidden': true,
      className: classNames(inputSize, {
        disabled: props.disabled,
        'empty-placeholder': props.placeholder == null,
      }),
    };
  }, [inputSize, props.disabled, props.placeholder]);

  return {
    fieldWrapperId,
    inputPropsFactory,
    countryPrefixProps,
    flagProps,
    maybeErrorOrDescriptionProps,
    disabled: props.disabled ?? false,
    readOnly: props.readOnly ?? false,
    inputSize,
    hasError: !!errorMessage,
    style: parseCssSizeVar('input-width', props.width),
    onValueChange,
  };
}
